import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { billingApi } from '../api/billing';
import { useAuthStore } from '../store/authStore';
import ReferralCodeInput from '../components/ReferralCodeInput';
import ReferralCodeApplied from '../components/ReferralCodeApplied';

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user, fetchCurrentUser } = useAuthStore();
  const [loading, setLoading] = useState<string | null>(null);

  const { data: prices } = useQuery({
    queryKey: ['billing', 'prices'],
    queryFn: billingApi.getPrices,
  });

  const { data: status } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: billingApi.getStatus,
    enabled: isAuthenticated,
    refetchOnMount: 'always',
  });

  const handleSubscribe = async (priceId: string, planType: string) => {
    if (!isAuthenticated) {
      navigate('/register?redirect=/pricing');
      return;
    }

    setLoading(planType);
    try {
      const { url } = await billingApi.createCheckout(priceId, i18n.language);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleStartTrial = async () => {
    if (!isAuthenticated) {
      navigate('/register?redirect=/pricing');
      return;
    }

    setLoading('trial');
    try {
      await billingApi.startTrial();
      await fetchCurrentUser();
      navigate('/home');
    } catch (error) {
      console.error('Failed to start trial:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoading('portal');
    try {
      const { url } = await billingApi.createPortal(i18n.language);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
    } finally {
      setLoading(null);
    }
  };

  // If user has active paid subscription, show manage button
  if (status?.status === 'active') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 py-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto mb-8">
          <Link
            to={isAuthenticated ? '/home' : '/'}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('pricing.back', 'Back')}
          </Link>
        </div>
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-white mb-4">
              {t('pricing.subscriptionActive', 'Subscription Active')}
            </h1>

            <p className="text-gray-400 mb-6">
              {status.currentPeriodEnd && (
                <>
                  {status.cancelAtPeriodEnd
                    ? t('pricing.expiresOn', 'Your subscription expires on')
                    : t('pricing.renewsOn', 'Your subscription renews on')}{' '}
                  {new Date(status.currentPeriodEnd).toLocaleDateString()}
                </>
              )}
            </p>

            <button
              onClick={handleManageSubscription}
              disabled={loading === 'portal'}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-bold rounded-xl transition-all disabled:opacity-50"
            >
              {loading === 'portal'
                ? t('pricing.loading', 'Loading...')
                : t('pricing.manageSubscription', 'Manage Subscription')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isTrialing = status?.status === 'trialing';

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <Link
            to={isAuthenticated ? '/home' : '/'}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('pricing.back', 'Back')}
          </Link>
          {!isAuthenticated && (
            <Link
              to="/login"
              className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
            >
              {t('nav.signin', 'Sign In')}
            </Link>
          )}
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-white via-primary-200 to-secondary-200 bg-clip-text text-transparent mb-4">
            {t('pricing.title', 'Choose Your Plan')}
          </h1>
          <p className="text-xl text-gray-400">
            {isTrialing || status?.trialUsed
              ? t('pricing.subtitleTrialing', 'Select a plan to continue after your trial ends')
              : t('pricing.subtitle', 'Start with a 7-day free trial, no credit card required')}
          </p>
        </div>

        {/* Trial info banner for trialing users */}
        {isTrialing && status.trialEndsAt && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-primary-500/20 to-secondary-500/20 backdrop-blur-xl border border-primary-400/50 rounded-2xl p-6 text-center">
              <p className="text-gray-300">
                {t('pricing.trialEnds', 'Your trial ends on')}{' '}
                <span className="text-white font-semibold">
                  {new Date(status.trialEndsAt).toLocaleDateString()}
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Referral Code Section - show for authenticated users without active subscription and no code yet */}
        {isAuthenticated && !user?.referredBy && status?.status !== 'active' && (
          <div className="mb-8">
            <ReferralCodeInput variant="card" />
          </div>
        )}

        {/* Show applied referral code */}
        {isAuthenticated && user?.referredBy && status?.status !== 'active' && (
          <div className="mb-8">
            <ReferralCodeApplied code={user.referredBy} />
          </div>
        )}

        {/* Trial Card - only show for users who haven't used trial yet */}
        {!isTrialing && !status?.trialUsed && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-primary-500/20 to-secondary-500/20 backdrop-blur-xl border border-primary-400/50 rounded-3xl p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('pricing.freeTrial', '7-Day Free Trial')}
              </h2>
              <p className="text-gray-300 mb-6">
                {t('pricing.freeTrialDescription', 'Try all features free for 7 days. No credit card required.')}
              </p>
              <button
                onClick={handleStartTrial}
                disabled={loading === 'trial'}
                className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-glow disabled:opacity-50"
              >
                {loading === 'trial'
                  ? t('pricing.starting', 'Starting...')
                  : t('pricing.startTrial', 'Start Free Trial')}
              </button>
            </div>
          </div>
        )}

        {/* Paid Plans */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Monthly */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-white/20 transition-all">
            <h3 className="text-xl font-bold text-white mb-2">
              {t('pricing.monthly', 'Monthly')}
            </h3>
            <div className="mb-4">
              <span className="text-4xl font-extrabold text-white">€{prices?.amountMonthly ?? 6}</span>
              <span className="text-gray-400">/{t('pricing.month', 'month')}</span>
            </div>
            <p className="text-gray-400 mb-6">
              {t('pricing.monthlyDescription', 'Billed monthly, cancel anytime')}
            </p>
            <button
              onClick={() => prices?.monthly && handleSubscribe(prices.monthly, 'monthly')}
              disabled={!prices?.monthly || loading === 'monthly'}
              className="w-full px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-xl transition-all disabled:opacity-50"
            >
              {loading === 'monthly'
                ? t('pricing.loading', 'Loading...')
                : t('pricing.subscribe', 'Subscribe')}
            </button>
          </div>

          {/* Yearly */}
          <div className="relative bg-gradient-to-br from-primary-500/10 to-secondary-500/10 backdrop-blur-xl border border-primary-400/30 rounded-3xl p-8 hover:border-primary-400/50 transition-all">
            <div className="absolute -top-3 right-4">
              <span className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-sm font-bold px-4 py-1 rounded-full">
                {t('pricing.save', 'Save 17%')}
              </span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t('pricing.yearly', 'Yearly')}
            </h3>
            <div className="mb-4">
              <span className="text-4xl font-extrabold text-white">€{prices?.amountYearly ?? 60}</span>
              <span className="text-gray-400">/{t('pricing.year', 'year')}</span>
            </div>
            <p className="text-gray-400 mb-6">
              {t('pricing.yearlyDescription', '2 months free, best value')}
            </p>
            <button
              onClick={() => prices?.yearly && handleSubscribe(prices.yearly, 'yearly')}
              disabled={!prices?.yearly || loading === 'yearly'}
              className="w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-bold rounded-xl transition-all shadow-lg disabled:opacity-50"
            >
              {loading === 'yearly'
                ? t('pricing.loading', 'Loading...')
                : t('pricing.subscribe', 'Subscribe')}
            </button>
          </div>
        </div>

        {/* Features List */}
        <div className="mt-12 text-center">
          <h3 className="text-lg font-semibold text-white mb-4">
            {t('pricing.allPlansInclude', 'All plans include:')}
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              t('pricing.feature1', 'Unlimited music streaming'),
              t('pricing.feature2', 'All business profiles'),
              t('pricing.feature3', 'License-free music'),
              t('pricing.feature4', 'Radio mode'),
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-300">
                <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
